import { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext(null);

function readStored() {
  try {
    const stored = localStorage.getItem("exopilot_theme");
    if (stored === "dark" || stored === "light") return stored;
  } catch (e) { /* localStorage unavailable */ }
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStored);

  const setTheme = (next) => {
    setThemeState(next);
    try { localStorage.setItem("exopilot_theme", next); } catch (e) { /* ignore */ }
  };

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  // Deliberately does NOT touch document.documentElement here -- only the
  // Landing page currently has dark: styling. Applying the class globally would
  // also flip Radix-portaled content and any page without dark: variants (console,
  // Privacy/Terms/Trust) into an inconsistent half-dark state. useApplyTheme()
  // below scopes the actual DOM effect to while a dark-mode-aware page is mounted.
  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}

/** Call once from the root of any page that has been given dark: styling --
 * applies/removes the "dark" class on <html> only while that page is mounted, so
 * pages without dark-mode support are unaffected and Radix portals (which mount
 * under document.body, a descendant of <html>) still pick up the class. */
export function useApplyTheme() {
  const { theme } = useTheme();
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    return () => document.documentElement.classList.remove("dark");
  }, [theme]);
}
